/**
 * Shared Time Card period stats (daily + customer/cost-code breakdown + OT totals).
 * Mirrors Dashboard Time Card / backend overtime rules.
 */

import type { Entry } from "@/lib/api";
import { hoursToTenThousandths, hundredthsToHours, tenThousandthsToHundredths } from "@/lib/hoursMath";
import { isCaliforniaCustomerId, sumOtTotalsCaAndNonCa, sumWeeklyOtTotals } from "@/lib/overtime";

export type DayCustomerBreakdown = {
  total: number;
  byCostCode: Record<string, number>;
};

export type DayBreakdown = {
  total: number;
  byCustomer: Record<string, DayCustomerBreakdown>;
};

export type TimecardPeriodStats = {
  byDay: Record<string, DayBreakdown>;
  totals: { regular: number; ot1: number; ot2: number; unp: number };
  totalHours: number;
};

/** Local calendar dates from start..end inclusive (YYYY-MM-DD). */
export function getDatesInPeriod(start: string, end: string): string[] {
  const dates: string[] = [];
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  const current = new Date(sy, sm - 1, sd);
  const endDate = new Date(ey, em - 1, ed);
  while (current <= endDate) {
    const y = current.getFullYear();
    const m = String(current.getMonth() + 1).padStart(2, "0");
    const d = String(current.getDate()).padStart(2, "0");
    dates.push(`${y}-${m}-${d}`);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export function weekdayShort(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short" });
}

export type ComputeTimecardOptions = {
  /**
   * When `entry.customer_code` is missing, resolve CA OT from Customer.id (FK) →
   * business customer_id string (e.g. "ca-site-1"). Same fallback as Dashboard.
   */
  customerCodeById?: Map<number, string> | Record<number, string>;
};

function resolveCustomerCode(
  e: Entry,
  customerCodeById?: ComputeTimecardOptions["customerCodeById"],
): string | null | undefined {
  if (e.customer_code) return e.customer_code;
  if (e.customer_id == null || !customerCodeById) return null;
  if (customerCodeById instanceof Map) return customerCodeById.get(e.customer_id) ?? null;
  return customerCodeById[e.customer_id] ?? null;
}

/**
 * Daily breakdown + period OT from closed punches.
 * Non-CA: weekly after 40h Sun–Sat. CA (customer_code prefix "ca-"): daily OT after 8h.
 * Punches stay 4 dp internally; each calendar day is Q2 (hundredths) before OT —
 * same paid-time unit as payroll batch and Employee Touch Detail.
 */
export function computeTimecardPeriodStats(
  entries: Entry[],
  options?: ComputeTimecardOptions,
): TimecardPeriodStats {
  const byDay: Record<string, DayBreakdown> = {};
  const caDayUnits: Record<string, number> = {};
  const nonCaDayUnits: Record<string, number> = {};
  const dayCustomerUnits: Record<string, Record<string, { total: number; byCostCode: Record<string, number> }>> =
    {};

  for (const e of entries) {
    if (!e.recorded_time || e.duration == null) continue;
    if (e.clock_out_time == null) continue;
    if (e.invalid_punch) continue;
    if (e.duration < 0) continue;

    const day = e.recorded_time.split("T")[0];
    const units = hoursToTenThousandths(e.duration);
    if (units <= 0) continue;
    const customer = e.customer_name || "Unspecified";
    const cc = e.cost_code_name || "Unspecified";

    if (!dayCustomerUnits[day]) dayCustomerUnits[day] = {};
    if (!dayCustomerUnits[day][customer]) {
      dayCustomerUnits[day][customer] = { total: 0, byCostCode: {} };
    }
    dayCustomerUnits[day][customer].total += units;
    dayCustomerUnits[day][customer].byCostCode[cc] =
      (dayCustomerUnits[day][customer].byCostCode[cc] || 0) + units;

    if (isCaliforniaCustomerId(resolveCustomerCode(e, options?.customerCodeById))) {
      caDayUnits[day] = (caDayUnits[day] || 0) + units;
    } else {
      nonCaDayUnits[day] = (nonCaDayUnits[day] || 0) + units;
    }
  }

  const allDays = new Set([...Object.keys(caDayUnits), ...Object.keys(nonCaDayUnits)]);
  let totalCents = 0;
  for (const day of allDays) {
    const caC = tenThousandthsToHundredths(caDayUnits[day] || 0);
    const nonC = tenThousandthsToHundredths(nonCaDayUnits[day] || 0);
    const dayCents = caC + nonC;
    totalCents += dayCents;
    const byCustomer: DayBreakdown["byCustomer"] = {};
    for (const [cust, data] of Object.entries(dayCustomerUnits[day] || {})) {
      const byCostCode: Record<string, number> = {};
      for (const [k, v] of Object.entries(data.byCostCode)) {
        byCostCode[k] = hundredthsToHours(tenThousandthsToHundredths(v));
      }
      byCustomer[cust] = {
        total: hundredthsToHours(tenThousandthsToHundredths(data.total)),
        byCostCode,
      };
    }
    byDay[day] = {
      total: hundredthsToHours(dayCents),
      byCustomer,
    };
  }

  const caDayHours: Record<string, number> = {};
  const nonCaDayHours: Record<string, number> = {};
  for (const [d, u] of Object.entries(caDayUnits)) {
    caDayHours[d] = hundredthsToHours(tenThousandthsToHundredths(u));
  }
  for (const [d, u] of Object.entries(nonCaDayUnits)) {
    nonCaDayHours[d] = hundredthsToHours(tenThousandthsToHundredths(u));
  }

  const totals =
    Object.keys(caDayHours).length > 0
      ? sumOtTotalsCaAndNonCa(caDayHours, nonCaDayHours)
      : sumWeeklyOtTotals(nonCaDayHours);

  return {
    byDay,
    totals: {
      regular: totals.regular,
      ot1: totals.ot1,
      ot2: totals.ot2,
      unp: 0,
    },
    totalHours: hundredthsToHours(totalCents),
  };
}

export function dayHasBreakdown(dayData: DayBreakdown | undefined): boolean {
  if (!dayData) return false;
  return Object.keys(dayData.byCustomer || {}).some(
    (c) => Object.keys(dayData.byCustomer[c]?.byCostCode || {}).length > 0,
  );
}

/** Group punches by UTC calendar day (recorded_time date). Includes duration 0 / open / invalid. */
export function punchesByCalendarDay(entries: Entry[]): Record<string, Entry[]> {
  const byDay: Record<string, Entry[]> = {};
  for (const e of entries) {
    if (!e.recorded_time) continue;
    const day = e.recorded_time.split("T")[0];
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(e);
  }
  for (const day of Object.keys(byDay)) {
    byDay[day].sort((a, b) => (a.recorded_time || "").localeCompare(b.recorded_time || ""));
  }
  return byDay;
}
